import React from 'react';

import { cn } from 'lib/utils';

interface StrapiIconProps {
  /** SVG/image URL from Strapi */
  url: string;
  /** Alternative text for accessibility */
  alt?: string;
  /** Additional CSS classes */
  className?: string;
  /** Icon size (width and height) */
  size?: number | string;
}

/**
 * StrapiIcon - Renders Strapi icons with theme support
 *
 * Uses CSS filter to adapt white/black icons to the current theme.
 * In light theme: white icons become dark
 * In dark theme: icons keep their original color
 *
 * Note: This approach is necessary because Strapi's S3 does not have
 * CORS configured to allow SVG fetching. CSS filter works
 * with <img> tags that don't have CORS restrictions.
 *
 * @example
 * ```tsx
 * <StrapiIcon
 *   url="https://strapi.example.com/uploads/icon.svg"
 *   alt="Menu icon"
 *   className="w-6 h-6"
 * />
 * ```
 */
export const StrapiIcon: React.FC<StrapiIconProps> = ({ url, alt = '', className, size = 24 }) => {
  if (!url) return null;

  const sizeValue = typeof size === 'number' ? `${size}px` : size;

  return (
    <img
      src={url}
      alt={alt}
      className={cn(
        // Base styles
        'strapi-icon',
        // Light theme: brightness(0) makes the icon black
        // Dark theme: no filter, keeps original color (white)
        'dark:filter-none',
        // Filtro aplicado via CSS abaixo
        className
      )}
      style={{
        width: sizeValue,
        height: sizeValue,
      }}
      // Prevent accidental drag
      draggable={false}
    />
  );
};

export default StrapiIcon;
